/**
 * THE ONE RENDERER of the `Problem` shape. No page or component renders a `Problem` itself.
 * Keyed on `ClassifiedProblem.kind`; copy from `domain/labels.ts`; colour from the tokens.
 * Never a stack trace — always the `instance`, small, so a report can name the call.
 */
import { signOut } from "../../api/session";
import type { ClassifiedProblem } from "../../domain/problem";
import { problemActions, problemCopy } from "../../domain/labels";
import { Button } from "../ui/button";

export interface ProblemAlertProps {
  problem: ClassifiedProblem | null;
  onRetry?: () => void;
  compact?: boolean;
}

/** The headline and the body for a classified problem. Pure — the test reads this. */
export function problemMessage(classified: ClassifiedProblem): { title: string; body: string } {
  const { kind, problem } = classified;
  const copy = problemCopy[kind];

  switch (kind) {
    case "forbidden":
      return {
        title: copy.title,
        body: copy.body.replace(
          "{permission}",
          classified.requiredPermission ?? problem?.requiredPermission ?? "permission",
        ),
      };
    /* `conflict` shows `detail` VERBATIM, as the whole message: it names what conflicted. The
       two derived-output kinds do the same: their `detail` names the run and why. */
    case "conflict":
    case "not_found":
    case "derived_pending":
    case "derived_unavailable":
    case "unsupported_upload":
      return { title: copy.title, body: problem?.detail ?? copy.body };
    case "validation":
      return { title: copy.title, body: (classified.unmapped ?? []).join(" ") };
    default:
      return { title: copy.title, body: copy.body };
  }
}

export function ProblemAlert({ problem, onRetry, compact }: ProblemAlertProps) {
  if (!problem) return null;
  const { kind } = problem;
  const { title, body } = problemMessage(problem);
  const unmapped = kind === "validation" ? (problem.unmapped ?? []) : [];
  const showRetry = (kind === "server" || kind === "network") && !!onRetry;

  return (
    <div
      role="alert"
      className={[
        "rounded-md border border-state-run-failed-fg/30 bg-state-run-failed-bg text-state-run-failed-fg",
        compact ? "p-sm" : "p-md",
      ].join(" ")}
    >
      <p className="type-subhead">{title}</p>

      {kind === "validation" ? (
        unmapped.length > 0 ? (
          <ul className="mt-xs list-disc pl-md">
            {unmapped.map((message, i) => (
              <li key={i} className="type-body">
                {message}
              </li>
            ))}
          </ul>
        ) : null
      ) : body ? (
        <p className="type-body mt-xs">{body}</p>
      ) : null}

      {kind === "unauthenticated" ? (
        /* A button, not a link: `signOut` ends the session and loads `/login` itself. */
        <button
          type="button"
          onClick={() => void signOut()}
          className="type-body mt-sm inline-block underline"
        >
          {problemCopy.unauthenticated.action}
        </button>
      ) : null}

      {showRetry ? (
        <Button className="mt-sm" size="sm" variant="outline" onClick={onRetry}>
          {problemActions.retry}
        </Button>
      ) : null}

      {problem.problem?.instance ? (
        <p className="type-caption mt-xs opacity-80">
          <span className="type-key">{problem.problem.instance}</span>
        </p>
      ) : null}
    </div>
  );
}
